import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Pagination } from 'react-bootstrap';
import './App.css';
import MemoListPage from './pages/MemoListPage';
import MemoDetailPage from './pages/MemoDetailPage';

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20];
const DEFAULT_ITEMS_PER_PAGE = 10;

const STORAGE_KEY = 'memo_app_memos';

function loadMemos() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveMemos(memos) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(memos));
}

function App() {
  const [memos, setMemos] = useState(loadMemos);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState({ title: '', content: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  useEffect(() => {
    saveMemos(memos);
  }, [memos]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchScope]);

  const handleNewMemo = (onCreated) => {
    const newMemo = {
      id: String(Date.now()),
      title: '',
      content: '',
      createdAt: Date.now(),
    };

    if (editingId !== null) {
      const saveCurrent = window.confirm('현재 메모를 저장하시겠습니까?');
      if (saveCurrent) {
        setMemos((prev) => [
          newMemo,
          ...prev.map((m) =>
            m.id === editingId
              ? { ...m, title: editingDraft.title, content: editingDraft.content }
              : m
          ),
        ]);
      } else {
        const isEmpty =
          !editingDraft.title.trim() && !editingDraft.content.trim();
        setMemos((prev) =>
          isEmpty
            ? [newMemo, ...prev.filter((m) => m.id !== editingId)]
            : [newMemo, ...prev]
        );
      }
      setEditingId(newMemo.id);
      setEditingDraft({ title: '', content: '' });
      onCreated?.(newMemo.id);
      return;
    }

    setMemos((prev) => [newMemo, ...prev]);
    setEditingId(newMemo.id);
    setEditingDraft({ title: '', content: '' });
    onCreated?.(newMemo.id);
  };

  const handleEdit = (id) => {
    const memo = memos.find((m) => m.id === id);
    setEditingId(id);
    setEditingDraft(
      memo ? { title: memo.title, content: memo.content } : { title: '', content: '' }
    );
  };

  const handleSave = (id, title, content) => {
    setMemos((prev) =>
      prev.map((m) => (m.id === id ? { ...m, title, content } : m))
    );
    setEditingId(null);
    setEditingDraft({ title: '', content: '' });
  };

  const handleDelete = (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    setMemos((prev) => prev.filter((m) => m.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setEditingDraft({ title: '', content: '' });
    }
  };

  const filteredMemos = useMemo(
    () =>
      searchQuery.trim()
        ? memos.filter((m) => {
            const q = searchQuery.trim();
            if (searchScope === 'title') return m.title.includes(q);
            if (searchScope === 'content') return m.content.includes(q);
            return m.title.includes(q) || m.content.includes(q);
          })
        : memos,
    [memos, searchQuery, searchScope]
  );

  const totalPages = Math.max(1, Math.ceil(filteredMemos.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const paginatedMemos = filteredMemos.slice(pageStart, pageStart + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const paginationItems = () => {
    const items = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);
    if (end - start + 1 < showPages) start = Math.max(1, end - showPages + 1);
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <MemoListPage
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                searchScope={searchScope}
                setSearchScope={setSearchScope}
                paginatedMemos={paginatedMemos}
                filteredMemos={filteredMemos}
                pageStart={pageStart}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
                setItemsPerPage={setItemsPerPage}
                setCurrentPage={setCurrentPage}
                handleNewMemo={handleNewMemo}
                handlePageChange={handlePageChange}
                paginationItems={paginationItems}
              />
            }
          />
          <Route
            path="/memo/:id"
            element={
              <MemoDetailPage
                memos={memos}
                editingId={editingId}
                editingDraft={editingDraft}
                setEditingDraft={setEditingDraft}
                handleEdit={handleEdit}
                handleSave={handleSave}
                handleDelete={handleDelete}
              />
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
